import { ReviewAttachments } from "./ReviewAttachments";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useBriefDocument, useApproveBrief, useNudgeBrief, useRejectBrief, useSubmitPublishedUrl } from "../../hooks/useCampaigns";
import { useEditOutput } from "../../hooks/useOutputDetail";
import { useAccountConnections } from "../../hooks/useAccountConnections";
import { useIdentities, useSurfaces } from "../../hooks/useSurfaces";
import { CopyButton } from "../ui/CopyButton";
import { Markdown } from "../agent/Markdown";
import { OutputView } from "../output/OutputView";
import { GroundingIndicator } from "../output/GroundingIndicator";
import { BriefProvenanceAlerts } from "./BriefProvenanceAlerts";
import { BlogRevisionFeedback } from "./BlogRevisionFeedback";
import { AssetReferencesPreview } from "./AssetReferencesPreview";
import { BlogPostPreview, BlogPublishPanels, isBlogBrief } from "./BlogPublishPanels";
import { PreviewButton } from "../preview/PreviewButton";
import type { Brief, ReviewApprovalItem } from "../../../../shared/types";
import { platformForSurface, surfaceLabel } from "../../lib/campaign-surfaces";
import { resolveLinkedInDestination } from "../../lib/review-destinations";
import { buildAccountConnectionsPath, buildBrandScopedPath } from "../../lib/brand-navigation";
import { claimRiskMessage, hasClaimRiskBlockedTrust, reviewTrustBadgeCopy } from "../../lib/review-trust-state";
import { Badge } from "../ui/Badge";
import { LoadErrorState } from "../ui/LoadState";
import { isNotFoundError } from "../../lib/load-state";
import {
  getBlockingReviewAlertDetails,
  getBlockingReviewAlerts,
  hasBlockingSourceRisk,
} from "../../../../shared/review-source-risk";
import type { OutputDocument } from "../../../../shared/types";

interface Props {
  brief: ReviewApprovalItem;
  brandId: string;
  onPublished?: (brief: Brief) => void;
  /** Called once the approval is recorded (the brief then leaves the Review queue). */
  onApproved?: (brief: Brief) => void;
}

const PASTE_BACK_WINDOW_MS = 30 * 60 * 1000;

function safeRecordedUrl(rawValue: string | null | undefined): string | null {
  if (!rawValue?.trim()) return null;
  try {
    const parsed = new URL(rawValue);
    return parsed.protocol === "http:" || parsed.protocol === "https:" ? parsed.toString() : null;
  } catch {
    return null;
  }
}

function buildPerformanceFollowupPrompt(brief: Brief, publishedUrl: string): string {
  const surfaceLabel = brief.surface ?? brief.platform ?? "content";
  return [
    `Review the published ${surfaceLabel} item "${brief.title}".`,
    `Published URL: ${publishedUrl}`,
    "Capture the first performance signals or operator observations you have.",
    "Then update durable brand learnings, packet context, or future brief guidance if this result should change how we write or publish.",
  ].join("\n");
}

function copyableDraftBody(document: OutputDocument | null | undefined, fallback: string): string {
  if (!document) return fallback;
  if (document.format === "text" || document.format === "markdown") return document.body;
  return fallback;
}

function splitPrependedDraftMetadata(rawValue: string): { body: string; chips: Array<{ label: string; value: string }> } {
  const lines = rawValue.split(/\r?\n/);
  const chips: Array<{ label: string; value: string }> = [];
  let cursor = 0;
  for (; cursor < lines.length; cursor += 1) {
    const match = /^(Status|Channel|Audience):\s*(.+)$/i.exec(lines[cursor] ?? "");
    if (!match) break;
    chips.push({ label: match[1]!, value: match[2]!.trim() });
  }
  if (chips.length === 0 || (lines[cursor] ?? "").trim() !== "---") {
    return { body: rawValue, chips: [] };
  }
  return {
    body: lines.slice(cursor + 1).join("\n").replace(/^\s+/, ""),
    chips,
  };
}

export function ApproveAndPostLinkedIn({ brief, brandId, onPublished, onApproved }: Props) {
  const navigate = useNavigate();
  const [publishedUrl, setPublishedUrl] = useState(brief.published_url ?? "");
  const [approvedAt, setApprovedAt] = useState<number | null>(null);
  const [timeLeft, setTimeLeft] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [sourceRiskAcknowledged, setSourceRiskAcknowledged] = useState(false);
  const [sourceRiskReason, setSourceRiskReason] = useState("");
  const [savedGrounding, setSavedGrounding] = useState(brief.grounding ?? null);
  const [locallyApproved, setLocallyApproved] = useState(false);

  const draftQ = useBriefDocument(brief.campaign_id, brief.id, "draft", true);
  const approve = useApproveBrief(brief.campaign_id, brandId);
  const nudge = useNudgeBrief(brief.campaign_id, brandId);
  const reject = useRejectBrief(brief.campaign_id, brandId);
  const submitUrl = useSubmitPublishedUrl(brief.campaign_id, brandId);
  const surfacesQ = useSurfaces(brandId);
  const identitiesQ = useIdentities(brandId);
  const connectionsQ = useAccountConnections(brandId);
  const editOutput = useEditOutput(
    draftQ.data?.data?.document_key ? "campaign_document" : undefined,
    draftQ.data?.data?.document_key,
  );

  // Approving content the operator cannot see is not a decision. Only a 404
  // means "no draft"; any other failure leaves the draft unknown.
  const draftUnknown = draftQ.isLoading || (draftQ.isError && !isNotFoundError(draftQ.error));
  const isWorking =
    approve.isPending || nudge.isPending || reject.isPending || submitUrl.isPending || brief.blog_revision_run?.status === "running";
  const isBlog = isBlogBrief(brief);
  // Match Review routing and approved-brief projection: explicit surface wins, including Other.
  // Platform is a legacy fallback only when surface is absent; stale platform data cannot reroute it.
  const surface = (brief.surface ?? brief.platform ?? "").trim().toLowerCase();
  const isLinkedIn = platformForSurface(surface) === "linkedin";

  const rawDraftContent = draftQ.data?.data?.content ?? "";
  const normalizedDraft = draftQ.data?.data?.output_document ?? null;
  const rawDraftText = copyableDraftBody(normalizedDraft, rawDraftContent);
  const draftMetadata = splitPrependedDraftMetadata(rawDraftText);
  const draftContent = !normalizedDraft ? draftMetadata.body : rawDraftContent;
  const displayDraft = normalizedDraft && (normalizedDraft.format === "text" || normalizedDraft.format === "markdown")
    ? { ...normalizedDraft, body: splitPrependedDraftMetadata(normalizedDraft.body).body }
    : normalizedDraft;
  const canInlineEdit = Boolean(displayDraft)
    && (brief.state === "drafting" || brief.state === "voice_review" || brief.state === "in_review");
  const draftText = draftMetadata.body;
  const { url: linkedinUrl, label: openLinkedInLabel } = resolveLinkedInDestination(
    surface.replaceAll("-", "_"),
    {
      surfaces: surfacesQ.data?.data,
      identities: identitiesQ.data?.data,
    },
  );
  const blockingAlerts = getBlockingReviewAlerts(brief.provenance_alerts);
  const blockingRiskSummary = getBlockingReviewAlertDetails(brief.provenance_alerts);
  const isSourceRiskBlocked = hasBlockingSourceRisk(brief.provenance_alerts);
  const isClaimRiskBlocked = hasClaimRiskBlockedTrust(brief);
  const trustCopy = reviewTrustBadgeCopy(brief);
  const blockingRunId = blockingAlerts.find((alert) => alert.run_id)?.run_id ?? null;
  const canProceedWithAcknowledgedRisk = !isSourceRiskBlocked || (sourceRiskAcknowledged && sourceRiskReason.trim().length > 0);
  const sourceRiskMessage = blockingAlerts[0]?.detail ?? "Resolve source risk before copying or posting.";
  const claimRiskDetail = claimRiskMessage(brief);
  const briefSurfaceLabel = surfaceLabel(brief.surface);
  const recordedPublishedUrl = brief.published_url?.trim() ?? "";
  const publishedHref = safeRecordedUrl(recordedPublishedUrl);
  const recordedPublishedAtValue = brief.published_at;
  const recordedPublishedAt = recordedPublishedAtValue
    ? new Date(recordedPublishedAtValue).toLocaleString()
    : null;
  const isApprovedForPublish = locallyApproved || brief.state === "approved" || brief.state === "scheduled";
  const needsApproval = brief.state === "in_review" && !locallyApproved;
  // After approval a blog post is previewed as it will be published (hero, title, body, social card).
  const approvedPreviewBody = displayDraft
    ? (displayDraft.format === "text" || displayDraft.format === "markdown" ? displayDraft.body : "")
    : draftContent;
  const showApprovedPreview = isBlog && isApprovedForPublish && !draftUnknown && !isSourceRiskBlocked && !isClaimRiskBlocked
    && approvedPreviewBody.trim().length > 0;
  const needsPublishedUrl = isLinkedIn && !recordedPublishedUrl
    && (approvedAt !== null || isApprovedForPublish);
  const performancePrompt = recordedPublishedUrl
    ? buildPerformanceFollowupPrompt(brief, recordedPublishedUrl)
    : null;
  const performanceFollowupHref = `/agent/${encodeURIComponent(brandId)}?new=1&briefId=${encodeURIComponent(brief.id)}`;
  const connectionsHref = buildAccountConnectionsPath(brandId);
  const linkedInConnections = (connectionsQ.data?.data ?? []).filter((connection) => connection.platform === "linkedin");
  const isLinkedInConnected = linkedInConnections.some((connection) =>
    connection.status === "connected"
    && connection.assignments.some((assignment) => assignment.brand_id === brandId),
  );
  const isConnectionGatePending = connectionsQ.isLoading || !connectionsQ.data;
  const isPublishConnectionBlocked = isLinkedIn && needsPublishedUrl && !isLinkedInConnected;
  const primaryActionLabel = recordedPublishedUrl
    ? "Open agent follow-up"
    : needsPublishedUrl
      ? "Mark published"
      : isSourceRiskBlocked
        ? blockingRunId
          ? "Inspect source run"
          : "Fix provenance"
        : isClaimRiskBlocked
          ? "Request safer rewrite"
          : needsApproval
            ? "Approve"
            : isLinkedIn ? openLinkedInLabel : "Approved";

  const primaryHelperText = recordedPublishedUrl
    ? "Capture follow-up observations now that the published URL is recorded."
    : needsPublishedUrl
      ? "Paste the final LinkedIn URL and record publication."
      : isSourceRiskBlocked
        ? isBlog ? "Repair or inspect provenance before approving this blog post." : "Repair or inspect provenance before treating this draft as publish-ready."
        : isClaimRiskBlocked
          ? isBlog ? "Request a safer rewrite before approving this blog post." : isLinkedIn ? "Request a safer rewrite before copying or opening LinkedIn." : "Request a safer rewrite before using this draft."
          : isBlog
            ? needsApproval
              ? "After approval, mark it Ready to publish so your site agent can claim it and publish it to the site."
              : null // the publish handoff panel carries the approved-state status
            : isLinkedIn
              ? needsApproval
                ? "Approve this draft before opening LinkedIn or recording publication."
                : "Open the destination surface once the draft is ready to publish."
              : needsApproval
                ? "Review and approve this draft before using it. Approval does not publish or schedule it."
                : "Handle publication separately for this surface; approval here does not publish or schedule it.";

  useEffect(() => {
    setSavedGrounding(brief.grounding ?? null);
  }, [brief.grounding]);

  useEffect(() => {
    setLocallyApproved(false);
    setApprovedAt(null);
  }, [brief.id]);

  useEffect(() => {
    if (!approvedAt) {
      setTimeLeft(null);
      return;
    }
    let id: ReturnType<typeof setInterval>;
    const tick = () => {
      const elapsed = Date.now() - approvedAt;
      const remaining = PASTE_BACK_WINDOW_MS - elapsed;
      if (remaining <= 0) {
        setTimeLeft("Window expired");
        clearInterval(id);
        return;
      }
      const mins = Math.floor(remaining / 60000);
      const secs = Math.floor((remaining % 60000) / 1000);
      setTimeLeft(`${mins}:${secs.toString().padStart(2, "0")} to paste URL`);
    };
    tick();
    id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [approvedAt]);

  async function handleApprove() {
    setError(null);
    if (isSourceRiskBlocked && !canProceedWithAcknowledgedRisk) {
      setError(sourceRiskMessage);
      return;
    }
    try {
      await approve.mutateAsync(
        isSourceRiskBlocked
          ? {
            briefId: brief.id,
            sourceRiskAcknowledgement: { reason: sourceRiskReason.trim() },
          }
          : brief.id,
      );
      setLocallyApproved(true);
      onApproved?.(brief);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Approve failed");
    }
  }

  async function handleOpenLinkedIn() {
    if (!isLinkedIn) return;
    setError(null);
    if (!isApprovedForPublish) {
      setError("Approve this draft before opening LinkedIn.");
      return;
    }
    if (!isLinkedInConnected) {
      setError("Connect LinkedIn before publishing this draft.");
      return;
    }
    if (isSourceRiskBlocked && !canProceedWithAcknowledgedRisk) {
      setError(sourceRiskMessage);
      return;
    }
    try {
      const opened = window.open(linkedinUrl, "_blank", "noopener,noreferrer");
      if (!opened) {
        setError("Popup blocked — please allow popups and try again, or open LinkedIn manually.");
      } else {
        setApprovedAt(Date.now());
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Open LinkedIn failed");
    }
  }

  async function handleNudge() {
    setError(null);
    try {
      const result = await nudge.mutateAsync({ briefId: brief.id, message: reason.trim() || undefined });
      navigate(result.data.chat_path);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Nudge failed");
    }
  }

  async function handleReject() {
    setError(null);
    try {
      await reject.mutateAsync({ briefId: brief.id, reason: reason.trim() || "Rejected by operator" });
      setReason("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Reject failed");
    }
  }

  async function handleMarkPublished() {
    setError(null);
    try {
      const trimmedPublishedUrl = publishedUrl.trim();
      const result = await submitUrl.mutateAsync({ briefId: brief.id, publishedUrl: trimmedPublishedUrl });
      const nowIso = new Date().toISOString();
      const persistedBrief = result.data.brief ?? {
        ...brief,
        state: "published",
        scheduled_at: null,
        blocked_reason: null,
        published_url: trimmedPublishedUrl,
        published_at: nowIso,
        updated_at: nowIso,
      };
      onPublished?.(persistedBrief);
      setPublishedUrl(persistedBrief.published_url ?? trimmedPublishedUrl);
      setApprovedAt(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Mark published failed");
    }
  }

  async function handleSaveOutput(document: OutputDocument) {
    setError(null);
    const documentKey = draftQ.data?.data?.document_key;
    if (!documentKey) {
      setError("Draft document is not available for editing yet.");
      return;
    }
    try {
      const result = await editOutput.mutateAsync({ output_document: document });
      setSavedGrounding(result.data.grounding);
      await draftQ.refetch();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    }
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">{brief.title}</h2>
        <p className="mt-0.5 text-xs text-slate-500">
          {briefSurfaceLabel} ·{" "}
          {brief.scheduled_at
            ? new Date(brief.scheduled_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })
            : "Unscheduled"}
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Badge variant={trustCopy.variant}>{trustCopy.label}</Badge>
          <p className="text-xs text-slate-600">{trustCopy.body}</p>
        </div>
        {/* Read-only preview as the audience will see it (works for drafts too). */}
        <div className="mt-2"><PreviewButton brandId={brandId} brief={brief} /></div>
      </div>

      <GroundingIndicator grounding={savedGrounding} />
      {draftQ.data?.data?.document_key && <ReviewAttachments brandId={brandId} campaignId={brief.campaign_id} briefId={brief.id}
        document={draftQ.data.data} editable={['drafting', 'in_review', 'approved'].includes(brief.state)}
        onSaved={async () => { setLocallyApproved(false); await draftQ.refetch(); }} />}
      <BriefProvenanceAlerts alerts={brief.provenance_alerts} mode="blocking" />

      {draftMetadata.chips.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {draftMetadata.chips.map((chip) => (
            <span
              key={`${chip.label}:${chip.value}`}
              className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-700"
            >
              <span className="text-slate-500">{chip.label}</span>: {chip.value}
            </span>
          ))}
        </div>
      )}

      {draftQ.isLoading ? (
        <p className="text-xs text-slate-400">Loading draft…</p>
      ) : draftUnknown ? (
        <LoadErrorState
          compact
          title="The draft could not be loaded"
          description="Approval is paused until the draft loads."
          onRetry={() => void draftQ.refetch()}
        />
      ) : showApprovedPreview ? (
        <BlogPostPreview brief={brief} brandId={brandId} content={approvedPreviewBody} />
      ) : displayDraft ? (
        <OutputView
          document={displayDraft}
          readAsMarkdown={isBlog}
          titleAlreadyShown={brief.title}
          emptyMessage="No draft document found."
          editable={canInlineEdit}
          isSaving={editOutput.isPending}
          onSave={handleSaveOutput}
        />
      ) : draftContent ? (
        <div className="rounded-md border border-slate-100 bg-slate-50 p-3">
          {isBlog ? <Markdown content={draftContent} reading titleAlreadyShown={brief.title} /> : <p className="whitespace-pre-wrap text-sm text-slate-800">{draftContent}</p>}
        </div>
      ) : (
        <p className="text-xs text-slate-400 italic">No draft document found.</p>
      )}

      {/\[\[media:[0-9a-f-]{36}\]\]/i.test(draftText) && <AssetReferencesPreview brandId={brandId} content={draftText} />}

      {brief.utm_campaign && (
        <div className="flex items-center gap-2 rounded-md border border-blue-100 bg-blue-50 p-2">
          <span className="text-xs text-blue-700 font-mono break-all">
            utm_campaign={brief.utm_campaign}
          </span>
        </div>
      )}

      <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
        <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Primary action</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {recordedPublishedUrl ? (
            <a
              href={performanceFollowupHref}
              className="inline-flex items-center rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700"
            >
              {primaryActionLabel}
            </a>
          ) : needsPublishedUrl ? (
            <button
              type="button"
              onClick={handleMarkPublished}
              disabled={isWorking || isPublishConnectionBlocked || !publishedUrl.trim()}
              className="rounded-md bg-green-700 px-3 py-1.5 text-xs font-medium text-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              {primaryActionLabel}
            </button>
          ) : isSourceRiskBlocked ? (
            blockingRunId ? (
              <button
                type="button"
                onClick={() => navigate(buildBrandScopedPath(`/runs/${blockingRunId}`, brandId))}
                className="rounded-md bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700"
              >
                {primaryActionLabel}
              </button>
            ) : (
              <span className="inline-flex items-center rounded-md bg-amber-100 px-3 py-1.5 text-xs font-medium text-amber-900">
                {primaryActionLabel}
              </span>
            )
          ) : isClaimRiskBlocked ? (
            <button
              type="button"
              onClick={handleNudge}
              disabled={isWorking}
              title={claimRiskDetail}
              className="rounded-md bg-amber-600 px-3 py-1.5 text-xs font-medium text-white disabled:cursor-not-allowed disabled:opacity-60 hover:bg-amber-700"
            >
              {primaryActionLabel}
            </button>
          ) : !isLinkedIn && !needsApproval ? null : (
            <button
              type="button"
              onClick={needsApproval ? handleApprove : isLinkedIn ? handleOpenLinkedIn : undefined}
              disabled={(!isLinkedIn && !needsApproval) || (!canProceedWithAcknowledgedRisk) || isClaimRiskBlocked || isWorking || brief.state === "published" || draftUnknown}
              title={isSourceRiskBlocked ? sourceRiskMessage : undefined}
              className={`rounded-md px-3 py-1.5 text-xs font-medium text-white disabled:cursor-not-allowed disabled:opacity-60 ${isLinkedIn ? "bg-[#0A66C2] hover:bg-[#004182]" : "bg-brand-600 hover:bg-brand-700"}`}
            >
              {primaryActionLabel}
            </button>
          )}
          {needsPublishedUrl && (
            <button
              type="button"
              onClick={handleOpenLinkedIn}
              disabled={isWorking || isPublishConnectionBlocked}
              className="rounded-md border border-sky-300 bg-white px-3 py-1.5 text-xs font-medium text-sky-800 disabled:cursor-not-allowed disabled:opacity-60 hover:bg-sky-50"
            >
              {openLinkedInLabel}
            </button>
          )}
          {isLinkedIn && isPublishConnectionBlocked && (
            <a
              href={connectionsHref}
              className="inline-flex items-center rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
            >
              {isConnectionGatePending ? "Checking LinkedIn connection" : "Connect LinkedIn first"}
            </a>
          )}
          {primaryHelperText && <p className="text-xs text-slate-500">{primaryHelperText}</p>}
        </div>
        {/* PRD-0190: a blog post is handed off to the brand's site agent, which publishes it and reports the URL. */}
        {isBlog && !isSourceRiskBlocked && !isClaimRiskBlocked && (
          <div className="mt-3">
            <BlogPublishPanels brief={brief} brandId={brandId} canMarkReady={isApprovedForPublish && !draftUnknown} />
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {draftText && (
          <CopyButton
            text={draftText}
            label="Copy text"
            disabled={(isSourceRiskBlocked) || isClaimRiskBlocked}
            title={isSourceRiskBlocked ? sourceRiskMessage : isClaimRiskBlocked ? claimRiskDetail : undefined}
          />
        )}
        {isBlog ? <BlogRevisionFeedback brief={brief} brandId={brandId} /> : <button
          type="button"
          onClick={handleNudge}
          disabled={isWorking}
          className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          Request edits
        </button>}
        <button
          type="button"
          onClick={handleReject}
          disabled={isWorking}
          className="rounded-md border border-red-300 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-800 disabled:cursor-not-allowed disabled:opacity-60"
        >
          Reject
        </button>
      </div>

      {isLinkedIn && isSourceRiskBlocked && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-3">
          <label className="flex items-start gap-2 text-sm text-amber-900">
            <input
              type="checkbox"
              checked={sourceRiskAcknowledged}
              onChange={(event) => setSourceRiskAcknowledged(event.target.checked)}
              className="mt-0.5"
            />
            <span>I understand this draft has blocking source risk and want to proceed to LinkedIn anyway.</span>
          </label>
          <textarea
            value={sourceRiskReason}
            onChange={(event) => setSourceRiskReason(event.target.value)}
            placeholder="Why are you proceeding despite the missing source evidence?"
            className="min-h-[72px] w-full rounded-md border border-amber-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 outline-none"
          />
          <p className="text-xs text-amber-800">
            Acknowledgement only unlocks opening LinkedIn. The dominant action remains inspecting or repairing provenance first.
          </p>
          <ul className="list-disc pl-5 text-xs text-amber-900">
            {blockingRiskSummary.map((detail) => (
              <li key={detail}>{detail}</li>
            ))}
          </ul>
        </div>
      )}

      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Optional note for nudge or reject"
        className="min-h-[56px] w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 outline-none"
      />

      {isSourceRiskBlocked && (
        <p className="text-xs font-medium text-amber-800">Resolve source risk before using this draft.</p>
      )}

      {recordedPublishedUrl && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-950">
          <p className="font-semibold">Published URL recorded</p>
          {recordedPublishedAt && <p className="mt-1 text-xs text-emerald-800">Recorded {recordedPublishedAt}</p>}
          {publishedHref ? (
            <a href={publishedHref} target="_blank" rel="noreferrer" className="mt-2 block break-all font-medium text-emerald-900 underline">
              {recordedPublishedUrl}
            </a>
          ) : (
            <p className="mt-2 break-all font-medium text-emerald-900">{recordedPublishedUrl}</p>
          )}
          <p className="mt-2 text-xs text-emerald-800">
            Next: verify the final output and start performance follow-up for this post.
          </p>
          {performancePrompt && (
            <div className="mt-3 flex flex-wrap gap-2">
              <CopyButton text={performancePrompt} label="Copy follow-up prompt" />
              <a
                href={performanceFollowupHref}
                className="inline-flex items-center rounded-md border border-emerald-300 bg-white px-3 py-1.5 text-xs font-medium text-emerald-900 hover:bg-emerald-100"
              >
                Open agent follow-up
              </a>
            </div>
          )}
        </div>
      )}

      {needsPublishedUrl && (
        <div className="space-y-1.5">
          {isLinkedIn && isPublishConnectionBlocked && (
            <p className="text-xs font-medium text-amber-800">
              LinkedIn must be connected before publishing.{" "}
              <a href={connectionsHref} className="underline">
                Connect LinkedIn first
              </a>
              .
            </p>
          )}
          {timeLeft && (
            <p className="text-xs font-medium text-amber-700">{timeLeft}</p>
          )}
          <div className="flex gap-2">
            <input
              value={publishedUrl}
              onChange={(e) => setPublishedUrl(e.target.value)}
              placeholder="Paste LinkedIn post URL after publishing"
              className="min-w-0 flex-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 outline-none"
            />
            <button
              type="button"
              onClick={handleMarkPublished}
              disabled={isWorking || isPublishConnectionBlocked || !publishedUrl.trim()}
              className="rounded-md border border-green-300 bg-green-50 px-3 py-2 text-xs font-medium text-green-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Save URL
            </button>
          </div>
        </div>
      )}

      {error && <p className="text-xs text-red-700">{error}</p>}
    </div>
  );
}
